import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { catchError, withLatestFrom } from 'rxjs/operators';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { ApiConsumptionRequestService } from '@core/api/services/consumption/api-consumption-request.service';
import { ConsumptionRequestMapper } from '@core/domain/mappers/consumption/consumption-request.mapper';
import { ApiClimateService } from '@core/api/services/consumption-invoices/api-climate.service';
import { add, format, startOfDay } from 'date-fns';
import { filterNil } from 'ngxtension/filter-nil';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadIndicatorService } from '@shared/components/download-indicator/download-indicator.service';
import { TranslateService } from '@ngx-translate/core';
import { appActions } from '@store/actions/app.actions';
import { consumptionInvoicesActions, ConsumptionInvoicesInformesActions, RehabActions } from '@store/actions/consumption-invoices.actions';
import { consumptionInvoicesSelectors } from '@store/selectors/consumption-invoices.selectors';
import { ConsumptionInvoicesFilter, ConsumptionInvoicesTabCode } from '@core/domain/model/consumption-invoices/consumption-invoices.model';
import { ConsumptionInvoicesMapper } from '@core/domain/mappers/consumption-invoices/consumption-invoices.mapper';
import { ApiAcsService } from '@core/api/services/consumption-invoices/api-acs.service';
import { userActions } from '@store/actions/user.actions';
import { ApiInformesUserService } from '@core/api/services/consumption-invoices/api-informes-user.service';

@Injectable()
export class ConsumptionInvoicesEffects {
  initializeFilter$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.initTab),
      // read both filters so we don't overwrite one when the other already exists
      withLatestFrom(
        this._store.select(consumptionInvoicesSelectors.climateFilter),
        this._store.select(consumptionInvoicesSelectors.acsFilter)
      ),
      map(([, climateFilter, acsFilter]) => {
        // if either filter already exists, don't initialize global defaults
        if (climateFilter != null || acsFilter != null) {
          return appActions.emptyAction();
        }
        const today = startOfDay(new Date());
        const filter: ConsumptionInvoicesFilter = {
          startDate: add(today, { months: -1 }),
          endDate: today,
          term: 'monthly',
        };
        return consumptionInvoicesActions.initFilter({ filter });
      })
    )
  );

  initializeTabs$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(consumptionInvoicesActions.initTab),
      map(() => consumptionInvoicesActions.changeTab({ index: 0 }))
    );
  });

  changeTab$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(consumptionInvoicesActions.changeTab),
      withLatestFrom(
        this._store.select(consumptionInvoicesSelectors.climateData),
        this._store.select(consumptionInvoicesSelectors.acsData)
      ),
      map(([{ index }, climate, acs]) => {
        if (index === 0 && climate == null) {
          return consumptionInvoicesActions.fetchClimateData();
        }
        if (index === 1 && acs == null) {
          return consumptionInvoicesActions.fetchAcsData();
        }
        return appActions.emptyAction();
      })
    );
  });

  // when a filter is set, trigger the corresponding data fetch to avoid race conditions
  triggerFetchOnSetFilter$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.setAcsFilter),
      map(() => consumptionInvoicesActions.fetchAcsData())
    )
  );

  triggerFetchOnSetClimateFilter$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.setClimateFilter),
      map(() => consumptionInvoicesActions.fetchClimateData())
    )
  );

  fetchRequestList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.fetchRequestList),
      switchMap(() =>
        this._apiRequest.fetchConsumptionRequest().pipe(
          map(ConsumptionRequestMapper.requestFromApiToDomain),
          map((value) => value.data),
          map((data) => consumptionInvoicesActions.setRequestList({ data })),
          catchError((error) => of(consumptionInvoicesActions.setRequestListError({ error: errorFromHttpErrorResponse(error) })))
        )
      )
    )
  );

  fetchInformesUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ConsumptionInvoicesInformesActions.fetchInformesUser),
      switchMap(() =>
        this._apiInformesUser.fetchInformesUser().pipe(
          map((response) => response.data),
          map((data) => ConsumptionInvoicesMapper.reportsFromApi(data)),
          map((data) => ConsumptionInvoicesInformesActions.setInformesUser({ data })),
          catchError((error: HttpErrorResponse) =>
            of(
              ConsumptionInvoicesInformesActions.errorInformesUser({
                error: errorFromHttpErrorResponse(error),
              })
            )
          )
        )
      )
    )
  );

  fetchClimate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.fetchClimateData),
      withLatestFrom(this._store.select(consumptionInvoicesSelectors.climateFilter).pipe(filterNil())),
      // eslint-disable-next-line consistent-return
      switchMap(([, filter]) => {
        console.debug('fetchClimate$ effect triggered with filter', filter);
        switch (filter.term) {
          case 'daily':
            const previousDaily = {
              ...filter,
              startDate: add(filter.startDate, { days: -1 }),
              endDate: add(filter.endDate, { days: -1 }),
            };
            return forkJoin([
              this._apiClimate.fetchDaily(ConsumptionInvoicesMapper.filterToApi(previousDaily)).pipe(catchError(() => of(null))),
              this._apiClimate.fetchDaily(ConsumptionInvoicesMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data ? ConsumptionInvoicesMapper.climateDailyToDomain(previousResponse.data) : null,
                currentData: currentResponse?.data ? ConsumptionInvoicesMapper.climateDailyToDomain(currentResponse.data) : null,
              }))
            );
          case 'monthly':
            const previousMonthly = {
              ...filter,
              startDate: add(filter.startDate, { months: -1 }),
              endDate: add(filter.endDate, { months: -1 }),
            };

            const currentYear = filter.startDate.getFullYear();
            const previousYear = previousMonthly.startDate.getFullYear();

            return forkJoin([
              this._apiClimate.fetchMonthly(ConsumptionInvoicesMapper.filterToApi(previousMonthly)).pipe(catchError(() => of(null))),
              this._apiClimate.fetchMonthly(ConsumptionInvoicesMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data
                  ? ConsumptionInvoicesMapper.climateMonthlyToDomain(previousResponse.data, previousYear)
                  : null,
                currentData: currentResponse?.data
                  ? ConsumptionInvoicesMapper.climateMonthlyToDomain(currentResponse.data, currentYear)
                  : null,
              }))
            );
        }
      }),
      map((value) => consumptionInvoicesActions.setClimateData(value))
    )
  );

  downloadClimate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(consumptionInvoicesActions.downloadClimateExcel),
        withLatestFrom(this._store.select(consumptionInvoicesSelectors.climateFilter).pipe(filterNil())),
        // eslint-disable-next-line consistent-return
        tap(([, filter]) => {
          switch (filter.term) {
            case 'daily':
              const dailyFilter = {
                ...filter,
                startDate: add(filter.startDate, { days: -1 }),
              };
              this._download.download(
                this._apiClimate.downloadExcelDaily(ConsumptionInvoicesMapper.filterToApi(dailyFilter)),
                this._getExcelFileName(dailyFilter, 'climate')
              );
              break;
            case 'monthly':
              const monthlyFilter = {
                ...filter,
                startDate: add(filter.startDate, { months: -1 }),
              };
              this._download.download(
                this._apiClimate.downloadExcelMonthly(ConsumptionInvoicesMapper.filterToApi(monthlyFilter)),
                this._getExcelFileName(monthlyFilter, 'climate')
              );
              break;
          }
        })
      ),
    { dispatch: false }
  );

  fetchAcs$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionInvoicesActions.fetchAcsData),
      withLatestFrom(this._store.select(consumptionInvoicesSelectors.acsFilter).pipe(filterNil())),
      // eslint-disable-next-line consistent-return
      switchMap(([, filter]) => {
        console.debug('fetchAcs$ effect triggered with filter', filter);
        switch (filter.term) {
          case 'daily':
            const previousDaily = {
              ...filter,
              startDate: add(filter.startDate, { days: -1 }),
              endDate: add(filter.endDate, { days: -1 }),
            };
            return forkJoin([
              this._apiAcs.fetchDaily(ConsumptionInvoicesMapper.filterToApi(previousDaily)).pipe(catchError(() => of(null))),
              this._apiAcs.fetchDaily(ConsumptionInvoicesMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data ? ConsumptionInvoicesMapper.acsDailyToDomain(previousResponse.data) : null,
                currentData: currentResponse?.data ? ConsumptionInvoicesMapper.acsDailyToDomain(currentResponse.data) : null,
              }))
            );
          case 'monthly':
            const previousMonthly = {
              ...filter,
              startDate: add(filter.startDate, { months: -1 }),
              endDate: add(filter.endDate, { months: -1 }),
            };
            const currentYear = filter.startDate.getFullYear();
            const previousYear = previousMonthly.startDate.getFullYear();
            return forkJoin([
              this._apiAcs.fetchMonthly(ConsumptionInvoicesMapper.filterToApi(previousMonthly)).pipe(catchError(() => of(null))),
              this._apiAcs.fetchMonthly(ConsumptionInvoicesMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data
                  ? ConsumptionInvoicesMapper.acsMonthlyToDomain(previousResponse.data, previousYear)
                  : null,
                currentData: currentResponse?.data ? ConsumptionInvoicesMapper.acsMonthlyToDomain(currentResponse.data, currentYear) : null,
              }))
            );
        }
      }),
      map((value) => consumptionInvoicesActions.setAcsData(value))
    )
  );

  downloadAcs$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(consumptionInvoicesActions.downloadAcsExcel),
        withLatestFrom(this._store.select(consumptionInvoicesSelectors.acsFilter).pipe(filterNil())),
        // eslint-disable-next-line consistent-return
        tap(([, filter]) => {
          switch (filter.term) {
            case 'daily':
              const dailyFilter = {
                ...filter,
                startDate: add(filter.startDate, { days: -1 }),
              };
              this._download.download(
                this._apiClimate.downloadExcelDaily(ConsumptionInvoicesMapper.filterToApi(dailyFilter)),
                this._getExcelFileName(dailyFilter, 'climate')
              );
              break;
            case 'monthly':
              const monthlyFilter = {
                ...filter,
                startDate: add(filter.startDate, { months: -1 }),
              };
              this._download.download(
                this._apiClimate.downloadExcelMonthly(ConsumptionInvoicesMapper.filterToApi(monthlyFilter)),
                this._getExcelFileName(monthlyFilter, 'climate')
              );
              break;
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Fetch housing climate
   */
  fetchHousingClimate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(RehabActions.fetchHousingClimate, userActions.setUser),
      switchMap((action) =>
        this._apiClimate.fetchHousingClimate(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(ConsumptionInvoicesMapper.housingFromApi),
          map((data) => RehabActions.setHousingClimate({ data })),
          catchError((err: HttpErrorResponse) => of(RehabActions.errorHousingClimate({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });

  /**
   * Fetch housing acs
   */
  fetchHousingAcs$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(RehabActions.fetchHousingAcs, userActions.setUser),
      switchMap((action) =>
        this._apiAcs.fetchHousingAcs(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(ConsumptionInvoicesMapper.housingFromApi),
          map((data) => RehabActions.setHousingAcs({ data })),
          catchError((err: HttpErrorResponse) => of(RehabActions.errorHousingAcs({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });

  /**
   * Fetch rooms climate
   */
  fetchRoomsClimate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(RehabActions.fetchRoomsClimate, userActions.setUser),
      switchMap((action) =>
        this._apiClimate.fetchRoomsClimate(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(ConsumptionInvoicesMapper.roomFromApi),
          map((data) => RehabActions.setRoomsClimate({ data })),
          catchError((err: HttpErrorResponse) => of(RehabActions.errorRoomsClimate({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });

  /**
   * Fetch rooms acs
   */
  fetchRoomsAcs$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(RehabActions.fetchRoomsAcs, userActions.setUser),
      switchMap((action) =>
        this._apiAcs.fetchRoomsAcs(action.type === userActions.setUser.type ? true : undefined).pipe(
          map((api) => api.data),
          map(ConsumptionInvoicesMapper.roomFromApi),
          map((data) => RehabActions.setRoomsAcs({ data })),
          catchError((err: HttpErrorResponse) => of(RehabActions.errorRoomsAcs({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });

  private _getExcelFileName(filter: ConsumptionInvoicesFilter, currentTab: ConsumptionInvoicesTabCode): string {
    const startDate = format(filter.startDate, 'dd-MM-yyyy');
    const endDate = format(filter.endDate, 'dd-MM-yyyy');
    const tabTranslation = this._translate.instant(`CONSUMPTION.TABS.${currentTab.toUpperCase()}`);
    const termTranslation = this._translate.instant(`GENERAL.${filter.term.toUpperCase()}`);
    return `${tabTranslation}_${termTranslation}_${startDate}_${endDate}`;
  }

  constructor(
    private readonly _actions$: Actions,
    private readonly _apiRequest: ApiConsumptionRequestService,
    private readonly _apiClimate: ApiClimateService,
    private readonly _apiAcs: ApiAcsService,
    private readonly _apiInformesUser: ApiInformesUserService,
    private readonly _store: Store<StoreState>,
    private readonly _download: DownloadIndicatorService,
    private readonly _translate: TranslateService
  ) {}
}
